import { Injectable } from '@angular/core';

import * as mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';
import * as _ from 'lodash';
import { IconLayerOptions, PolygonLayerOptions, LineLayerOptions } from './models';
import { Feature } from '@turf/turf';
import { CircleLayerOptions } from './models/circle-layer-options';

@Injectable({
    providedIn: 'root'
})
export class MapboxHelper {
    constructor() { }

    public loadImage(map: mapboxgl.Map, url: string, name: string, sdf = false): void {
        map.loadImage(url, (error, image) => {
            if (error) {
                console.log('could not load image');
            } else {
                if (sdf) {
                    map.addImage(name, image, { sdf: true });
                } else {
                    map.addImage(name, image);
                }
            }
        });
    }

    public addPopupWithText(map: mapboxgl.Map, location: [number, number], popup: mapboxgl.Popup, text: string) {
        popup.setLngLat(location)
            .setHTML(text)
            .setMaxWidth('')
            .addTo(map);
    }

    public closePopup(popup: mapboxgl.Popup) {
        popup.remove();
    }

    public createPopup(map: mapboxgl.Map): mapboxgl.Popup {
        return new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false
        });
    }

    public resetMapToNorth(map: mapboxgl.Map): void {
        map.resetNorth();
    }

    public panTo(map: mapboxgl.Map, location: [number, number], duration?: number) {
        if (duration) {
            map.panTo(location, { duration });
        } else {
            map.panTo(location);
        }
    }

    public centerPosition(map: mapboxgl.Map, location: [number, number], zoom: number = 14, padding?: mapboxgl.PaddingOptions) {
        zoom = zoom > 22 ? 22 : zoom;
        
        // Don't zoom out if current zoom is already closer
        const currentZoom = map.getZoom();
        const targetZoom = currentZoom > zoom ? currentZoom : zoom;
        
        map.flyTo({
            center: location,
            zoom: targetZoom,
            padding,
            speed: 3,
            curve: 1,
        });
    }

    public fitBoundsOfFeatureCollection(map: mapboxgl.Map, features: Feature[], padding?: mapboxgl.PaddingOptions): boolean {
        if (features.length === 0) {
            return false;
        }

        if (!padding) {
            padding = { top: 40, bottom: 40, left: 40, right: 40 };
        }

        const featureCollection = this._createFeatureCollection(features);

        const featureBbox: any = turf.bbox(featureCollection);
        const boundingBox = new mapboxgl.LngLatBounds(featureBbox);

        if (map.getZoom() < 14) {
            map.fitBounds(boundingBox, {
                padding,
                maxZoom: 14
            });
            return true;
        }

        const bbox: any = _.flatten(map.getBounds().toArray());
        const mapPolygon = turf.bboxPolygon(bbox);

        const boundingPolygon = turf.bboxPolygon(featureBbox);
        const isFeatureWithinViewport = turf.booleanContains(mapPolygon, boundingPolygon);

        if (!isFeatureWithinViewport) {
            map.fitBounds(boundingBox, {
                padding: padding,
                maxZoom: 14
            });
        }
        return true;
    }

    public setOpacityOnLayers(map: mapboxgl.Map, layerNames: string[], opacity: number) {
        _.each(layerNames, (layerName => {
            // get layer to determine opacity types
            const layer = map.getLayer(layerName);

            if (layer) {
                const paintProperties = [];
                switch (layer.type) {
                    case 'fill':
                        paintProperties.push('fill-opacity');
                        break;
                    case 'line':
                        paintProperties.push('line-opacity');
                        break;
                    case 'circle':
                        paintProperties.push('circle-opacity');
                        break;
                    case 'symbol':
                        paintProperties.push('icon-opacity');
                        paintProperties.push('text-opacity');
                        break;
                }

                _.each(paintProperties, paintProperty => map.setPaintProperty(layerName, paintProperty, opacity));
            }
        }));
    }

    public setVisibilityOnLayers(map: mapboxgl.Map, layerNames: string[], visible: boolean) {
        _.each(layerNames, (layerName => {
            // get layer to determine opacity types
            const layer = map.getLayer(layerName);

            if (layer) {
                const value = visible ? 'visible' : 'none';
                map.setLayoutProperty(layerName, 'visibility', value);
            }
        }));
    }

    public renderIcon(map: mapboxgl.Map, sourceId: string, sourceLayer: string, layerId: string, options: IconLayerOptions): string[] {
        const layerNames = [];
        // bara rendera om layerId inte finns
        if (!map.getLayer(layerId)) {
            if (options.layerBefore && !map.getLayer(options.layerBefore)) {
                options.layerBefore = undefined;
            }

            const layers = this._createIconLayer(sourceId, sourceLayer, layerId, options);
            // add the layers to the map
            _.each(layers, (layer: any) => {
                if (layer.interactive) {
                    layerNames.push(layer.id);
                }
                map.addLayer(layer, options.layerBefore);
            });
        }
        return layerNames;
    }

    public renderCircle(map: mapboxgl.Map, sourceId: string, sourceLayer: string, layerId: string, options: CircleLayerOptions): string[] {
        const layerNames = [];
        // bara rendera om layerId inte finns
        if (!map.getLayer(layerId)) {
            if (options.layerBefore && !map.getLayer(options.layerBefore)) {
                options.layerBefore = undefined;
            }

            const layers = this._createCircleLayer(sourceId, sourceLayer, layerId, options);
            // add the layers to the map
            _.each(layers, (layer: any) => {
                if (layer.interactive || layer.interactive === undefined) {
                    layerNames.push(layer.id);
                }
                map.addLayer(layer, options.layerBefore);
            });
        }
        return layerNames;
    }

    public renderLineLayer(map: mapboxgl.Map, sourceId: string, sourceLayer: string, layerId: string, options: LineLayerOptions): string[] {
        const layerNames = [];
        // bara rendera om layerId inte finns
        if (!map.getLayer(layerId)) {
            if (options.layerBefore && !map.getLayer(options.layerBefore)) {
                options.layerBefore = undefined;
            }

            const layers = this._createLineLayer(sourceId, sourceLayer, layerId, options);
            // add the layers to the map
            _.each(layers, (layer: any) => {
                if (layer.interactive) {
                    layerNames.push(layer.id);
                }
                map.addLayer(layer, options.layerBefore);
            });
        }
        return layerNames;
    }

    public renderPolygonLayer(map: mapboxgl.Map, sourceId: string, sourceLayer: string,
        layerId: string, options: PolygonLayerOptions): string[] {
        const layerNames = [];
        // bara rendera om layerId inte finns
        if (!map.getLayer(layerId)) {
            if (options.layerBefore && !map.getLayer(options.layerBefore)) {
                options.layerBefore = undefined;
            }
            const layers = this._createPolygonLayer(sourceId, sourceLayer, layerId, options);
            // add the layers to the map
            _.each(layers, (layer: any) => {
                if (layer.interactive) {
                    layerNames.push(layer.id);
                }
                map.addLayer(layer, options.layerBefore);
            });
        }
        return layerNames;
    }

    public createSource(map: mapboxgl.Map, data: GeoJSON.FeatureCollection<any> | string, sourceId: string, cluster: boolean = false) {
        const source = this._createGeoJsonSourceFromData(data);
        if (cluster) {
            source.cluster = true;
            source.clusterMaxZoom = 20;
            source.clusterRadius = 20;
        }
        map.addSource(sourceId, source);
    }

    public removeSource(map: mapboxgl.Map, sourceId: string) {
        if (this.sourceExists(map, sourceId)) {
            map.removeSource(sourceId);
        }
    }

    public createVectorSource(map: mapboxgl.Map, url: string, sourceId: string, promoteId?: string) {
        if (this.sourceExists(map, sourceId)) {
            return;
        }

        map.addSource(sourceId, {
            type: 'vector',
            url,
            promoteId
        });
    }

    public updateSourceData(map: mapboxgl.Map, data: GeoJSON.FeatureCollection<any> | string, sourceId: string): boolean {
        const layerSource: any = (map.getSource(sourceId));
        if (layerSource) {
            layerSource.setData(data);
            return true;
        }
        return false;
    }

    public sourceExists(map: mapboxgl.Map, sourceId: string): boolean {
        if (map && map.getSource(sourceId) as mapboxgl.AnySourceImpl) {
            return true;
        }
        return false;
    }

    public getVectorSourceUrl(map: mapboxgl.Map, sourceId: string): string {
        if (this.sourceExists(map, sourceId)) {
            const source = map.getSource(sourceId);

            if (source.type === 'vector') {
                return source.url;
            }
        }
        return undefined;
    }

    public layerExists(map: mapboxgl.Map, layerId: string): boolean {
        if (map.getLayer(layerId) as mapboxgl.Layer) {
            return true;
        }
        return false;
    }

    public removeLayers(map: mapboxgl.Map, layerNames: string[]) {
        _.each(layerNames, layerName => {
            if (map.getLayer(layerName)) {
                map.removeLayer(layerName);
            }
        });
    }

    //#region private helper methods
    private _createGeoJsonSourceFromData(data: GeoJSON.FeatureCollection<any> | string): mapboxgl.GeoJSONSourceRaw {
        return {
            type: 'geojson',
            data,
            promoteId: 'id'
        };
    }

    private _createLineLayer(sourceId: string, sourceLayer: string, layerId: string, options: LineLayerOptions): mapboxgl.Layer[] {
        const filter = ['all', ['==', '$type', 'LineString']];

        if (options.type) {
            filter.push(['==', 'stKey', options.type]);
        }
        if (options.customFilters) {
            _.each(options.customFilters, customFilter => filter.push(customFilter));
        }

        const layers = [];

        if (options.border) {
            const l1 = {
                id: layerId + 'border',
                type: 'line',
                source: sourceId,
                maxzoom: options.visibleTo || 24,
                minzoom: options.visibleFrom || 0,
                interactive: true,
                paint: {
                    'line-width': options.borderWidth || options.lineWidth,
                    'line-opacity': ['case',
                        ['has', 'dashed'], 0.0,
                        options.borderOpacity !== undefined ? options.borderOpacity : options.opacity !== undefined ? options.opacity : 1
                    ],
                    'line-color': options.borderColor || '#fff'
                },
                layout: {
                    visibility: options.visibility !== undefined ? (options.visibility ? 'visible' : 'none') : 'visible'
                },
                filter
            };

            if (options.sort) {
                l1.layout['line-sort-key'] = {
                    type: 'identity',
                    property: options.sort
                };
            }

            layers.push(l1);
        }

        const l2 = {
            id: layerId,
            type: 'line',
            source: sourceId,
            maxzoom: options.visibleTo || 24,
            minzoom: options.visibleFrom || 0,
            interactive: true,
            paint: {
                'line-opacity': ['case',
                    ['has', 'dashed'], 1,
                    options.opacity !== undefined ? options.opacity : 1
                ],
                'line-width': options.lineWidth
            },
            layout: {
                visibility: options.visibility !== undefined ? (options.visibility ? 'visible' : 'none') : 'visible'
            },
            filter
        };

        if (options.sort) {
            l2.layout['line-sort-key'] = {
                type: 'identity',
                property: options.sort
            };
        }

        l2.paint['line-dasharray'] = ['case',
            ['has', 'dashed'],
            ['literal', [1, 2]], // [dash-length, gap-length]
            ['literal', [1, 0]]
        ];
        l2.layout['line-cap'] = 'round';
        l2.paint['line-gap-width'] = 0;
        l2.layout['line-join'] = 'miter';

        l2.paint['line-color'] = [
            'case',
            ['==', ['feature-state', 'color'], null],
            options.color || options.fallbackColor,
            ['feature-state', 'color'],
        ];

        layers.push(l2);

        if (options.text) {
            const textLayer = {
                id: layerId + 'text',
                type: 'symbol',
                source: sourceId,
                interactive: true,
                paint: {
                    'text-color': '#333333',
                    'text-halo-width': 4,
                    'text-halo-color': 'rgba(255,255,255, 1.0)',
                    'text-opacity': options.opacity !== undefined ? options.opacity : 1,
                },
                maxzoom: options.visibleTo || 24,
                minzoom: options.visibleFrom || 0,
                layout: {
                    visibility: options.visibility !== undefined ? (options.visibility ? 'visible' : 'none') : 'visible',
                    'symbol-placement': 'line',
                    'symbol-spacing': 100,
                    'text-transform': 'uppercase',
                    'text-field': options.textField || '{title}',
                    'text-size': {
                        base: 1,
                        stops: [
                            [12, 11],
                            [15, 14],
                            [19, 16]
                        ]
                    },
                },
                filter
            };

            if (options.sort) {
                textLayer.layout['symbol-sort-key'] = {
                    type: 'identity',
                    property: options.sort
                };
            }

            layers.push(textLayer);
        }

        if (sourceLayer) {
            _.each(layers, layer => layer['source-layer'] = sourceLayer);
        }

        return layers;
    }

    private _createPolygonLayer(sourceId: string, sourceLayer: string, layerId: string, options: PolygonLayerOptions): mapboxgl.Layer[] {
        const filter = ['all', ['==', '$type', 'Polygon']];

        if (options.type) {
            filter.push(['==', 'stKey', options.type]);
        }
        if (options.customFilters) {
            _.each(options.customFilters, customFilter => filter.push(customFilter));
        }

        const layers = [];

        if (options.border) {
            layers.push(
                {
                    id: layerId + 'border',
                    type: 'line',
                    source: sourceId,
                    maxzoom: options.visibleTo || 24,
                    minzoom: options.visibleFrom || 0,
                    paint: {
                        'line-width': options.borderWidth ?? 3,
                        'line-color': options.borderColor ?? options.color,
                        'line-opacity': options.borderOpacity ?? options.opacity ?? 1,
                        'line-dasharray': options.borderDashed ? [3, 1] : []
                    },
                    layout: {
                        visibility: options.visibility !== undefined ? (options.visibility ? 'visible' : 'none') : 'visible'
                    },
                    interactive: true,
                    filter
                }
            );
        }

        layers.push(
            {
                id: layerId,
                type: 'fill',
                source: sourceId,
                maxzoom: options.visibleTo || 24,
                minzoom: options.visibleFrom || 0,
                interactive: true,
                paint: {
                    'fill-antialias': true,
                    'fill-color': options.color,
                    'fill-opacity': options.opacity
                },
                layout: {
                    visibility: options.visibility !== undefined ? (options.visibility ? 'visible' : 'none') : 'visible'
                },
                filter
            }
        );

        if (options.text) {
            layers.push({
                id: layerId + 'text',
                type: 'symbol',
                source: sourceId,
                interactive: true,
                paint: {
                    'text-color': options.textColor || '#333333',
                    'text-halo-width': options.textHaloWidth || 2,
                    'text-halo-color': options.textHaloColor || 'rgba(255,255,255, 1.0)'
                },
                maxzoom: options.visibleTo || 24,
                minzoom: options.visibleFrom || 0,
                layout: {
                    'symbol-placement': 'line',
                    'symbol-spacing': options.textSpacing || 500,
                    'text-font': options.textFont || ['Open Sans Regula'],
                    'text-field': options.textField || '{title}',
                    'text-size': options.textSize || [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        0,
                        10,
                        15,
                        11,
                        16,
                        12,
                        22,
                        12
                    ]
                },
                filter
            });
        }


        if (sourceLayer) {
            _.each(layers, layer => layer['source-layer'] = sourceLayer);
        }

        return layers;
    }

    private _createCircleLayer(sourceId: string, sourceLayer: string, layerId: string, options: CircleLayerOptions): mapboxgl.Layer[] {
        const filter: any = ['all'];

        if (options.customFilters) {
            _.each(options.customFilters, customFilter => filter.push(customFilter));
        }

        if (options.useDefaultPointFilter) {
            filter.push(['==', '$type', 'Point']);
        }

        const layer: mapboxgl.Layer = {
            id: layerId,
            type: 'circle',
            filter,
            maxzoom: options.visibleTo || 24,
            minzoom: options.visibleFrom || 0,
            source: sourceId,
            interactive: true,
            paint: {
            },
            layout: {
                visibility: options.visibility !== undefined ? (options.visibility ? 'visible' : 'none') : 'visible'
            }
        };

        if (options.sort) {
            layer.layout['circle-sort-key'] = {
                type: 'identity',
                property: options.sort
            };
        }

        if (options.offset) {
            layer.paint['circle-translate'] = options.offset;
        }

        if (options.color) {
            options.selectedColor = options.selectedColor ? options.selectedColor : options.color;
            layer.paint['circle-color'] = ['case',
                ['boolean', ['get', 'isSelected'], false],
                options.selectedColor,
                options.color
            ];

            if (options.useColorFromProperties) {
                layer.paint['circle-color'] = ['case',
                    ['has', 'iconColor'],
                    ['get', 'iconColor'],
                    ['case',
                        ['boolean', ['get', 'isSelected'], false],
                        options.selectedColor,
                        options.color
                    ]
                ];
            }
        }

        if (options.radius) {
            layer.paint['circle-radius'] = options.radius;
        }

        if (options.circleOpacity) {
            layer.paint['circle-opacity'] = options.circleOpacity;
        }

        if (sourceLayer) {
            layer['source-layer'] = sourceLayer;
        }

        if (options.strokeWidth) {
            layer.paint['circle-stroke-width'] = options.strokeWidth;
        }

        if (options.strokeColor) {
            layer.paint['circle-stroke-color'] = options.strokeColor;
        }

        if (options.strokeOpacity) {
            layer.paint['circle-stroke-opacity'] = options.strokeOpacity;
        }

        const layers = [];
        if (options.selectedCircleOptions) {
            const selectedOptionsFilter: any = _.cloneDeep(filter);
            selectedOptionsFilter.push(['==', 'isSelected', true]);
            const selectedLayer: mapboxgl.Layer = {
                id: layerId + '-selected',
                source: sourceId,
                maxzoom: options.visibleTo || 24,
                minzoom: options.visibleFrom || 0,
                interactive: false,
                type: 'circle',
                filter: selectedOptionsFilter,
                layout: {
                    visibility: options.visibility !== undefined ? (options.visibility ? 'visible' : 'none') : 'visible'
                },
                paint: {
                    'circle-radius': options.selectedCircleOptions.radius,
                    'circle-color': options.selectedCircleOptions.color,
                    'circle-opacity': options.selectedCircleOptions.opacity,
                    'circle-stroke-width': options.selectedCircleOptions.strokeWidth,
                    'circle-stroke-opacity': options.selectedCircleOptions.strokeOpacity,
                    'circle-stroke-color': options.selectedCircleOptions.strokeColor
                }
            };
            layers.push(selectedLayer);
        }

        layers.push(layer);
        return layers;
    }

    // Creates a icon with a optional selected layer behind that is transparent
    private _createIconLayer(sourceId: string, sourceLayer: string, layerId: string, options: IconLayerOptions): mapboxgl.Layer[] {
        const filter: any = ['all'];

        if (options.useDefaultPointFilter) {
            filter.push(['==', '$type', 'Point']);
        }

        if (options.type) {
            filter.push(['==', 'stKey', options.type]);
        }
        if (options.customFilters) {
            _.each(options.customFilters, customFilter => filter.push(customFilter));
        }

        const layers = [];

        if (options.selectedCircleOptions) {
            const selectedOptionsFilter: any = _.cloneDeep(filter);
            selectedOptionsFilter.push(['==', 'isSelected', true]);
            const selectedLayer: mapboxgl.Layer = {
                id: layerId + '-selected',
                source: sourceId,
                maxzoom: options.visibleTo || 24,
                minzoom: options.visibleFrom || 0,
                interactive: false,
                type: 'circle',
                filter: selectedOptionsFilter,
                layout: {
                    visibility: options.visibility !== undefined ? (options.visibility ? 'visible' : 'none') : 'visible'
                },
                paint: {
                    'circle-radius': options.selectedCircleOptions.radius,
                    'circle-color': options.selectedCircleOptions.color,
                    'circle-opacity': options.selectedCircleOptions.opacity,
                    'circle-stroke-width': options.selectedCircleOptions.strokeWidth,
                    'circle-stroke-opacity': options.selectedCircleOptions.strokeOpacity,
                    'circle-stroke-color': options.selectedCircleOptions.strokeColor
                }
            };

            if (options.symbolSortKey) {
                selectedLayer.layout['circle-sort-key'] = <any>{
                    type: 'identity',
                    property: options.symbolSortKey
                };
            }

            layers.push(selectedLayer);
        }

        const layer: mapboxgl.AnyLayer = {
            id: layerId,
            type: 'symbol',
            source: sourceId,
            filter,
            interactive: options.interactive,
            maxzoom: options.visibleTo || 24,
            minzoom: options.visibleFrom || 0,
            paint: {
                'icon-opacity': options.opacity !== undefined ? options.opacity : 1,
                'text-opacity': options.opacity !== undefined ? options.opacity : 1,
                // 'icon-halo-width': 0,
                // 'icon-halo-color': '#ffffff'
            },
            layout: {
                'text-allow-overlap': options.textAllowOverlap,
                'text-ignore-placement': false,
                'text-optional': true,
                'text-max-width': options.textMaxWidth || 6,
                visibility: options.visibility !== undefined ? (options.visibility ? 'visible' : 'none') : 'visible'
            }
        };

        if (options.icon) {
            // Handle icon and which icon to use. (if selectedIcon exists etc)
            let iconSelections = options.icon;
            options.selectedIcon = options.selectedIcon ? options.selectedIcon : options.icon;
            if (_.isString(options.icon)) {
                iconSelections = ['case',
                    ['boolean', ['get', 'isSelected'], false],
                    options.selectedIcon,
                    options.icon
                ];

                if (options.useIconFromProperties) {
                    iconSelections = ['case',
                        ['has', 'iconImage'],
                        ['get', 'iconImage'],
                        iconSelections
                    ];
                }

                if (options.useColorFromProperties) {
                    // selectedIconColor
                    layer.paint['icon-color'] = ['case',
                        ['has', 'iconColor'],
                        ['get', 'iconColor'],
                        ['case',
                            ['boolean', ['get', 'isSelected'], false],
                            options.selectedIconColor ? options.selectedIconColor : options.iconColor,
                            options.iconColor
                        ]];
                }

                if (options.iconOffset) {
                    layer.layout['icon-offset'] = options.iconOffset;
                }

                if (options.iconOpacity) {
                    layer.paint['icon-opacity'] = options.iconOpacity;
                }

                if (options.iconHaloColor) {
                    layer.paint['icon-halo-color'] = options.iconHaloColor;
                }

                if (options.iconHaloWidth) {
                    layer.paint['icon-halo-width'] = options.iconHaloWidth;
                }
            }

            const iconLayerLayerOptions = {
                'icon-image': iconSelections,
                'icon-size': options.iconSize || 0,
                'icon-allow-overlap': true,
                'icon-ignore-placement': true,
                'icon-rotation-alignment': options.iconRotationAlignment || 'map'
            };
            Object.assign(layer.layout, iconLayerLayerOptions);
        }

        if (options.symbolSortKey) {
            layer.layout['symbol-sort-key'] = <any>{
                type: 'identity',
                property: options.symbolSortKey
            };
        }

        if (options.text) {
            layer.layout['text-field'] = options.text;
        }

        if (options.iconRotate) {
            layer.layout['icon-rotate'] = {
                type: 'identity',
                property: 'bearing',
                default: 0
            };
        }

        if (options.textAnchor) {
            layer.layout['text-anchor'] = <any>options.textAnchor;
        }

        if (options.textColor) {
            layer.paint['text-color'] = options.textColor;
        }

        if (options.textOpacity) {
            layer.paint['text-opacity'] = options.textOpacity;
        }

        if (options.textHaloColor) {
            layer.paint['text-halo-color'] = options.textHaloColor;
        }

        if (options.textHaloWidth) {
            layer.paint['text-halo-width'] = options.textHaloWidth;
        }

        if (options.textOffset) {
            layer.layout['text-offset'] = options.textOffset;
        }

        if (options.textSize) {
            layer.layout['text-size'] = options.textSize;
        }

        if (options.textFont) {
            layer.layout['text-font'] = options.textFont;
        }

        if (options.textKeepUpRight) {
            layer.layout['text-keep-upright'] = options.textKeepUpRight;
        }

        if (options.symbolPlacement) {
            layer.layout['symbol-placement'] = <any>options.symbolPlacement;
        }

        if (options.symbolSpacing) {
            layer.layout['symbol-spacing'] = options.symbolSpacing;
        }

        if (sourceLayer) {
            layer['source-layer'] = sourceLayer;
        }

        layers.push(layer);
        return layers;
    }

    private _createFeatureCollection(features: any[] | any): any {
        if (!(features instanceof Array)) {
            features = [features];
        }
        return turf.featureCollection(features);
    }
}
